import { useMemo } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Typography,
} from "@mui/material";
import AccountTreeIcon from "@mui/icons-material/AccountTree";

import {
  getChildrenForSelection,
  getRevisionsFromDrawings,
} from "../utils/dataHelpers";

export const MATERIAL_TYPES = ["P&ID", "2D Drawing", "Estimation"];

/**
 * SelectionPanel - Left or right panel for selecting drawings to compare
 * Provides hierarchical dropdown selectors from plant down to sheet level
 * Shows breadcrumb trail of current selection
 *
 * @component
 * @param {Object} props
 * @param {string} props.side - "left" or "right" (for styling and labels)
 * @param {Object} props.selection - Current selection state { type, project, revision, drawing, sheet }
 * @param {Function} props.onChange - Callback when selection changes
 * @param {Object} props.plant - Plant object with projects hierarchy
 * @returns {JSX.Element} Selection panel component
 */
export function SelectionPanel({ side, selection, onChange, plant }) {
  const accent = side === "left" ? "primary" : "secondary";
  const borderColor = side === "left" ? "primary.main" : "secondary.main";

  // Get hierarchical data based on current selection using new utility functions
  const selectionData = useMemo(
    () => getChildrenForSelection(plant, selection),
    [plant, selection],
  );

  const { projects, drawings, revisions, sheets } = selectionData;

  /**
   * Handle field changes and reset dependent selections
   */
  const set = (field, val) => {
    if (field === "type")
      onChange({
        type: val,
        project: null,
        revision: null,
        drawing: null,
        sheet: null,
      });
    else if (field === "project")
      onChange({
        ...selection,
        project: val,
        revision: null,
        drawing: null,
        sheet: null,
      });
    else if (field === "revision")
      onChange({ ...selection, revision: val, drawing: null, sheet: null });
    else if (field === "drawing")
      onChange({ ...selection, drawing: val, sheet: null });
    else onChange({ ...selection, [field]: val });
  };

  // Deduplicate drawings by softwareId for the drawing dropdown
  const uniqueDrawings = useMemo(() => {
    const seen = new Set();
    return drawings.filter(
      (d) => !seen.has(d.softwareId) && seen.add(d.softwareId),
    );
  }, [drawings]);

  // Build breadcrumb trail
  const crumbs = useMemo(() => {
    const parts = [plant?.name || "— Plant —"];
    const proj = plant?.projects?.find((p) => p.id === selection.project);
    if (proj) parts.push(proj.name);
    if (selection.revision && !selection.drawing)
      parts.push(`Rev ${selection.revision}`);
    const selectedDwg = drawings.find(
      (d) => d.softwareId === selection.drawing,
    );
    if (selectedDwg) parts.push(selectedDwg.dwgNumber);
    return parts;
  }, [selection, drawings, plant]);

  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        p: 2.5,
        borderRadius: 2,
        borderColor,
        borderWidth: 1.5,
        display: "flex",
        flexDirection: "column",
        gap: 1.75,
      }}
    >
      {/* Header */}
      <Box
        display="flex"
        alignItems="center"
        gap={1}
        justifyContent="space-between"
      >
        <Box display="flex" alignItems="center" gap={1}>
          <AccountTreeIcon sx={{ color: borderColor, fontSize: 16 }} />
          <Typography
            variant="subtitle2"
            fontWeight={700}
            color={borderColor}
            sx={{
              textTransform: "uppercase",
              letterSpacing: 1,
              fontSize: "0.68rem",
            }}
          >
            {selection.type
              ? `Select ${selection.type} Materials`
              : "Select Materials"}
          </Typography>
        </Box>

        {/* Material Type Selector */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel>Type</InputLabel>
          <Select
            value={selection.type ?? ""}
            label="Type"
            color={accent}
            onChange={(e) => set("type", e.target.value || null)}
          >
            <MenuItem value="">
              <em>— Select Type —</em>
            </MenuItem>
            {MATERIAL_TYPES.map((type) => (
              <MenuItem key={type} value={type}>
                {type}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Project Selector */}
      {selection.type && (
        <FormControl fullWidth size="small">
          <InputLabel>Project</InputLabel>
          <Select
            value={selection.project ?? ""}
            label="Project"
            color={accent}
            onChange={(e) => set("project", e.target.value || null)}
          >
            <MenuItem value="">
              <em>— Plant Level —</em>
            </MenuItem>
            {projects.map((p) => (
              <MenuItem key={p.id} value={p.id}>
                {p.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Revision Selector - Only for P&ID and 2D Drawing (not Estimation) */}
      {selection.project &&
        selection.type !== "Estimation" &&
        revisions.length > 0 && (
          <FormControl fullWidth size="small">
            <InputLabel>Revision</InputLabel>
            <Select
              value={selection.revision ?? ""}
              label="Revision"
              color={accent}
              onChange={(e) => set("revision", e.target.value || null)}
            >
              <MenuItem value="">
                <em>Latest ({revisions[revisions.length - 1]})</em>
              </MenuItem>
              {revisions.map((r) => (
                <MenuItem key={r} value={r}>
                  Rev {r}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

      {/* Drawing Selector - Only for P&ID and 2D Drawing (not Estimation) */}
      {selection.project &&
        selection.type !== "Estimation" &&
        uniqueDrawings.length > 0 && (
          <FormControl fullWidth size="small">
            <InputLabel>Drawing</InputLabel>
            <Select
              value={selection.drawing ?? ""}
              label="Drawing"
              color={accent}
              onChange={(e) => set("drawing", e.target.value || null)}
            >
              <MenuItem value="">
                <em>— All Drawings —</em>
              </MenuItem>
              {uniqueDrawings.map((d) => (
                <MenuItem key={d.softwareId} value={d.softwareId}>
                  {d.dwgNumber} — {d.dwgTitle}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

      {/* Sheet Selector - Only for P&ID and 2D Drawing (not Estimation) */}
      {selection.drawing &&
        selection.type !== "Estimation" &&
        sheets.length > 1 && (
          <FormControl fullWidth size="small">
            <InputLabel>Sheet</InputLabel>
            <Select
              value={selection.sheet ?? ""}
              label="Sheet"
              color={accent}
              onChange={(e) => set("sheet", e.target.value || null)}
            >
              <MenuItem value="">
                <em>— All Sheets —</em>
              </MenuItem>
              {sheets.map((s) => (
                <MenuItem key={s.id} value={s.id}>
                  {s.sheetTitle} {s.sheetNumber}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}

      {/* Breadcrumb Trail */}
      <Stack direction="row" flexWrap="wrap" gap={0.5} mt="auto">
        {crumbs.map((c, i) => (
          <Chip
            key={i}
            label={c}
            size="small"
            variant={i === crumbs.length - 1 ? "filled" : "outlined"}
            color={i === crumbs.length - 1 ? accent : "default"}
            sx={{ fontSize: "0.62rem", height: 18 }}
          />
        ))}
      </Stack>
    </Paper>
  );
}
